import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Layout from "@/components/Layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Activity,
  Battery,
  Building2,
  Droplet,
  Heart,
  Loader,
  Power,
  RefreshCw,
  SlidersHorizontal,
  Waves,
  Zap,
} from "lucide-react";
import mqtt from "mqtt";

const MQTT_BROKER_URL =
  process.env.REACT_APP_MQTT_BROKER_URL || "wss://aquatank.aquasense.tech:9001";
const MQTT_USERNAME = process.env.REACT_APP_MQTT_USERNAME || "aqua-tank";
const MQTT_PASSWORD = process.env.REACT_APP_MQTT_PASSWORD || "fIExd783QL9z";
const MQTT_D2C_TOPIC = process.env.REACT_APP_MQTT_D2C_TOPIC || "AQUATANK/D2C/+";
const MQTT_STATE_TOPIC =
  process.env.REACT_APP_MQTT_STATE_TOPIC || "AQUATANK/STATE/+/+";
const MQTT_C2D_PREFIX = process.env.REACT_APP_MQTT_C2D_PREFIX || "AQUATANK/C2D";
const CONTROLLER_ACTIVE_WINDOW_MS = 5 * 60 * 1000;
const LIVE_STATUS_REFRESH_MS = 60 * 1000;
const MIN_COMMAND_PROCESSING_MS = 10 * 1000;
const PUMP_ACK_TIMEOUT_MS = 20 * 1000;
const VALVE_ACK_TIMEOUT_MS = 50 * 1000;

const modeOptions = ["manual", "auto", "remote"];

const normalize = (value) => String(value || "").toLowerCase();

const isUsableReference = (value) => {
  if (value === null || value === undefined) return false;

  const normalizedValue = String(value).trim().toLowerCase();
  return Boolean(
    normalizedValue &&
      !["-", "none", "null", "undefined"].includes(normalizedValue),
  );
};

const getUsableReference = (...values) => {
  const value = values.find(isUsableReference);
  return value === undefined ? "" : String(value).trim();
};

const formatControlTitle = (label, reference) =>
  reference ? `${label} (${reference})` : label;

const formatTimestamp = (timestamp) => {
  if (!timestamp) return "-";
  const isNumericTimestamp = typeof timestamp === "number";
  const date =
    isNumericTimestamp
      ? new Date(timestamp * 1000 - 5.5 * 60 * 60 * 1000)
      : new Date(timestamp);

  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getTimestampMs = (timestamp) => {
  if (!timestamp) return null;

  if (typeof timestamp === "number") {
    const timestampMs = timestamp * 1000;
    const now = Date.now();

    if (timestampMs - now > 5 * 60 * 1000) {
      return timestampMs - 5.5 * 60 * 60 * 1000;
    }

    return timestampMs;
  }

  const timestampMs = new Date(timestamp).getTime();
  return Number.isNaN(timestampMs) ? null : timestampMs;
};

const isControllerActive = (timestamp, nowMs) => {
  const timestampMs = getTimestampMs(timestamp);
  return Boolean(
    timestampMs &&
      nowMs - timestampMs >= 0 &&
      nowMs - timestampMs <= CONTROLLER_ACTIVE_WINDOW_MS,
  );
};

const isOn = (value) => ["on", "open", "true", "1"].includes(normalize(value));

const formatWholePercent = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? Math.round(numericValue) : "-";
};

const formatUgtLevel = (value) => {
  if (value === null || value === undefined || value === "" || value === "-") {
    return "-";
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? `${Math.round(numericValue)}%` : value;
};

const isStateTopic = (topic) => topic.startsWith("AQUATANK/STATE/");

const getHealthText = (health) => {
  if (!health) return "Unknown";
  return health === "no_fault" ? "Healthy" : health.replaceAll("_", " ");
};

const buildCommandPayload = ({
  deviceType,
  deviceId,
  command,
  mode,
  remoteMode,
  targetId,
  targetType,
  targetState,
}) => ({
  command_id: `${deviceId}-${Date.now()}`,
  device_id: deviceId,
  device_type: deviceType,
  config_type: "control",
  command,
  operation_mode: mode,
  remote_mode: remoteMode,
  target_type: targetType,
  target_id: targetId,
  target_state: targetState,
  timestamp: Math.floor(Date.now() / 1000),
});

const buildValveTogglePayload = ({
  deviceId,
  valveId,
  operation,
}) => ({
  device_id: deviceId,
  timestamp: Math.floor(Date.now() / 1000),
  mid: `${Date.now()}${Math.random().toString(36).slice(2, 8)}`,
  valve_id: valveId,
  operation,
});

const buildPumpTogglePayload = ({
  deviceId,
  pumpId,
  operation,
}) => ({
  device_id: deviceId,
  timestamp: Math.floor(Date.now() / 1000),
  mid: `${Date.now()}${Math.random().toString(36).slice(2, 8)}`,
  pump_id: pumpId,
  operation,
});

const buildRemoteOperationsPayload = ({
  deviceId,
  remoteOperations,
}) => ({
  device_id: deviceId,
  timestamp: Math.floor(Date.now() / 1000),
  mid: `${Date.now()}${Math.random().toString(36).slice(2, 8)}`,
  remote_operations: remoteOperations,
});

const Dashboard = () => {
  const { isSuperAdmin } = useAuth();
  const mqttClientRef = useRef(null);
  const pendingCommandTimersRef = useRef({});
  const pendingCommandsRef = useRef({});
  const statePacketQueueRef = useRef([]);
  const stateFlushTimerRef = useRef(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTower, setSelectedTower] = useState(null);
  const [allTowers, setAllTowers] = useState([]);
  const [mqttConnected, setMqttConnected] = useState(false);
  const [pendingCommands, setPendingCommands] = useState({});
  const [statusCheckTime, setStatusCheckTime] = useState(Date.now());

  useEffect(() => {
    pendingCommandsRef.current = pendingCommands;
  }, [pendingCommands]);

  const applyDevicePackets = useCallback((packets) => {
    const latestPackets = Array.from(
      packets
        .filter((packet) => packet?.device_id)
        .filter((packet) => {
          const packetTimestampMs = getTimestampMs(packet.timestamp);
          if (!packetTimestampMs) return true;

          return !Object.values(pendingCommandsRef.current).some(
            (command) =>
              command.deviceId === packet.device_id &&
              command.startedAt &&
              packetTimestampMs < command.startedAt,
          );
        })
        .reduce((map, packet) => {
          map.set(`${packet.device_type}:${packet.device_id}`, packet);
          return map;
        }, new Map())
        .values(),
    );

    if (latestPackets.length === 0) return;

    setStatusCheckTime(Date.now());

    setDashboardData((current) => {
      if (!current?.data) return current;

      let changed = false;
      let nextData = current.data;

      latestPackets.forEach((packet) => {
        nextData = nextData.map((tower) => {
        if (packet.device_type === "v_controller") {
          const isSameLevelController =
            tower.level_controller?.device_id === packet.device_id ||
            tower.tanks?.some(
              (tank) =>
                (tank.level_controller_device_id ||
                  tower.level_controller?.device_id) === packet.device_id,
            );

          if (!isSameLevelController) return tower;

          const statuses = Array.isArray(packet.status) ? packet.status : [];
          changed = true;

          return {
            ...tower,
            level_controller: {
              ...(tower.level_controller || {}),
              device_id: packet.device_id,
              battery_level: packet.battery_level,
              operation_mode: packet.operation_mode,
              remote_mode: packet.remote_mode,
              timestamp: packet.timestamp,
            },
            tanks: (tower.tanks || []).map((tank) => {
              const status = statuses.find(
                (item) =>
                  item.tank_id === tank.tank_id ||
                  item.valve_id === tank.valve_id,
              );

              if (!status) {
                return {
                  ...tank,
                  level_controller_mode:
                    packet.operation_mode || tank.level_controller_mode,
                  level_controller_remote:
                    packet.remote_mode || tank.level_controller_remote,
                  level_controller_battery:
                    packet.battery_level ?? tank.level_controller_battery,
                };
              }

              return {
                ...tank,
                tank_percentage:
                  status.tank_percentage ?? tank.tank_percentage,
                valve_state: status.valve_state || tank.valve_state,
                require_water: packet.require_water ?? tank.require_water,
                level_controller_mode:
                  packet.operation_mode || tank.level_controller_mode,
                level_controller_remote:
                  packet.remote_mode || tank.level_controller_remote,
                level_controller_battery:
                  packet.battery_level ?? tank.level_controller_battery,
              };
            }),
          };
        }

        if (packet.device_type === "p_controller") {
          const isSamePumpController =
            tower.pump_controller?.device_id === packet.device_id;

          if (!isSamePumpController) return tower;

          const statuses = Array.isArray(packet.status)
            ? packet.status
            : packet.pump_id
              ? [packet]
              : [];
          changed = true;

          return {
            ...tower,
            pump_controller: {
              ...(tower.pump_controller || {}),
              device_id: packet.device_id,
              battery_level:
                packet.battery_level ?? tower.pump_controller?.battery_level,
              operation_mode: packet.operation_mode,
              remote_mode: packet.remote_mode,
              timestamp: packet.timestamp,
            },
            tanks: (tower.tanks || []).map((tank) => {
              const pumpId =
                tank.pump_packet?.pump_id ||
                tank.pump?.pump_custom_id ||
                tank.pump?.pump_id;
              const status = statuses.find((item) => item.pump_id === pumpId);

              return {
                ...tank,
                pump_packet: status
                  ? {
                      ...(tank.pump_packet || {}),
                      ...status,
                      atbc_mode: packet.operation_mode,
                      atbc_remote: packet.remote_mode,
                    }
                  : tank.pump_packet,
              };
            }),
          };
        }

        return tower;
      });
      });

      if (!changed) return current;
      return { ...current, data: nextData, timestamp: new Date().toISOString() };
    });
  }, []);

  const flushStatePackets = useCallback(() => {
    stateFlushTimerRef.current = null;
    const packets = statePacketQueueRef.current;
    statePacketQueueRef.current = [];
    applyDevicePackets(packets);
  }, [applyDevicePackets]);

  const enqueueStatePacket = useCallback(
    (packet) => {
      statePacketQueueRef.current.push(packet);

      if (!stateFlushTimerRef.current) {
        stateFlushTimerRef.current = setTimeout(flushStatePackets, 150);
      }
    },
    [flushStatePackets],
  );

  const isSuccessfulAck = (ack) =>
    ["pass", "success"].includes(normalize(ack?.result));

  const completePendingCommand = useCallback(
    (commandId, { toastMessage, toastType = "success" } = {}) => {
      const pendingCommand = pendingCommandsRef.current[commandId];
      const elapsedMs = pendingCommand?.startedAt
        ? Date.now() - pendingCommand.startedAt
        : MIN_COMMAND_PROCESSING_MS;
      const remainingMs = Math.max(0, MIN_COMMAND_PROCESSING_MS - elapsedMs);

      if (pendingCommandTimersRef.current[commandId]) {
        clearTimeout(pendingCommandTimersRef.current[commandId]);
        delete pendingCommandTimersRef.current[commandId];
      }

      const finish = () => {
        const refNext = { ...pendingCommandsRef.current };
        delete refNext[commandId];
        pendingCommandsRef.current = refNext;

        setPendingCommands((current) => {
          const next = { ...current };
          delete next[commandId];
          return next;
        });

        if (toastMessage) {
          toastType === "error" ? toast.error(toastMessage) : toast.success(toastMessage);
        }
      };

      if (remainingMs > 0) {
        pendingCommandTimersRef.current[commandId] = setTimeout(finish, remainingMs);
      } else {
        finish();
      }
    },
    [],
  );

  const applyToggleAcknowledgement = useCallback((ack, pendingCommand) => {
    if (!pendingCommand || !isSuccessfulAck(ack)) return false;

    if (pendingCommand.type === "remote") {
      if (
        ack.device_id !== pendingCommand.deviceId ||
        ack.remote_operations !== pendingCommand.remoteOperations
      ) {
        return false;
      }

      setDashboardData((current) => {
        if (!current?.data) return current;

        const nextData = current.data.map((tower) => {
          if (pendingCommand.controllerType === "level") {
            const isMatchingLevelController =
              tower.level_controller?.device_id === pendingCommand.deviceId ||
              tower.tanks?.some(
                (tank) =>
                  (tank.level_controller_device_id ||
                    tower.level_controller?.device_id) === pendingCommand.deviceId,
              );

            if (!isMatchingLevelController) return tower;

            return {
              ...tower,
              level_controller: {
                ...(tower.level_controller || {}),
                remote_mode: pendingCommand.remoteOperations,
              },
              tanks: (tower.tanks || []).map((tank) => ({
                ...tank,
                level_controller_remote: pendingCommand.remoteOperations,
              })),
            };
          }

          if (
            pendingCommand.controllerType === "pump" &&
            tower.pump_controller?.device_id === pendingCommand.deviceId
          ) {
            return {
              ...tower,
              pump_controller: {
                ...(tower.pump_controller || {}),
                remote_mode: pendingCommand.remoteOperations,
              },
              tanks: (tower.tanks || []).map((tank) => ({
                ...tank,
                pump_packet: {
                  ...(tank.pump_packet || {}),
                  atbc_remote: pendingCommand.remoteOperations,
                },
              })),
            };
          }

          return tower;
        });

        return { ...current, data: nextData, timestamp: new Date().toISOString() };
      });

      return true;
    }

    const isValidBaseAck =
      ack.device_id === pendingCommand.deviceId &&
      ack.operation === pendingCommand.operation;

    if (!isValidBaseAck) return false;

    if (
      pendingCommand.type === "valve" &&
      ack.valve_id !== pendingCommand.targetId
    ) {
      return false;
    }

    if (
      pendingCommand.type === "pump" &&
      ack.pump_id !== pendingCommand.targetId
    ) {
      return false;
    }

    setDashboardData((current) => {
      if (!current?.data) return current;

      const nextData = current.data.map((tower) => ({
        ...tower,
        tanks: (tower.tanks || []).map((tank) => {
          if (
            pendingCommand.type === "valve" &&
            (tank.level_controller_device_id || tower.level_controller?.device_id) ===
              pendingCommand.deviceId &&
            tank.valve_id === pendingCommand.targetId
          ) {
            return {
              ...tank,
              valve_state:
                pendingCommand.operation === "open" ? "OPEN" : "CLOSED",
            };
          }

          const pumpId =
            tank.pump_packet?.pump_id ||
            tank.pump?.pump_custom_id ||
            tank.pump?.pump_id;

          if (
            pendingCommand.type === "pump" &&
            tower.pump_controller?.device_id === pendingCommand.deviceId &&
            pumpId === pendingCommand.targetId
          ) {
            return {
              ...tank,
              pump_packet: {
                ...(tank.pump_packet || {}),
                pump_id: pendingCommand.targetId,
                pump_state: pendingCommand.operation === "on" ? "ON" : "OFF",
              },
            };
          }

          return tank;
        }),
      }));

      return { ...current, data: nextData, timestamp: new Date().toISOString() };
    });

    return true;
  }, []);

  const clearPendingCommandImmediately = useCallback((commandId) => {
    if (pendingCommandTimersRef.current[commandId]) {
      clearTimeout(pendingCommandTimersRef.current[commandId]);
      delete pendingCommandTimersRef.current[commandId];
    }

    const refNext = { ...pendingCommandsRef.current };
    delete refNext[commandId];
    pendingCommandsRef.current = refNext;

    setPendingCommands((current) => {
      const next = { ...current };
      delete next[commandId];
      return next;
    });
  }, []);

  const handleMqttMessage = useCallback(
    (topic, message) => {
      try {
        const payload = JSON.parse(message.toString());

        if (isStateTopic(topic)) {
          enqueueStatePacket(payload);
          return;
        }

        if (payload.mid && payload.result) {
          const ackId = payload.mid;
          const pendingCommand = pendingCommandsRef.current[ackId];

          if (pendingCommand) {
            const isValidAck = applyToggleAcknowledgement(payload, pendingCommand);
            const ackPassed = isSuccessfulAck(payload);

            completePendingCommand(ackId, {
              toastMessage:
                isValidAck && ackPassed
                  ? "Device acknowledged command"
                  : ackPassed
                    ? "Invalid acknowledgement received"
                    : "Device rejected command",
              toastType: isValidAck && ackPassed ? "success" : "error",
            });
          }
          return;
        }

        if (payload.command_id) {
          completePendingCommand(payload.command_id, {
            toastMessage: "Device acknowledged command",
          });
        }

        if (
          payload.command_result ||
          payload.control_result ||
          payload.set_control_result
        ) {
          const result =
            payload.command_result ||
            payload.control_result ||
            payload.set_control_result;
          if (result === "pass" || result === "success") {
            toast.success("Device acknowledged command");
          } else {
            toast.error("Device rejected command");
          }
        }

        applyDevicePackets([payload]);
      } catch (error) {
        console.error("Invalid MQTT message", topic, error);
      }
    },
    [
      applyDevicePackets,
      applyToggleAcknowledgement,
      completePendingCommand,
      enqueueStatePacket,
    ],
  );

  useEffect(() => {
    const client = mqtt.connect(MQTT_BROKER_URL, {
      username: MQTT_USERNAME,
      password: MQTT_PASSWORD,
      reconnectPeriod: 2000,
      clean: true,
    });

    mqttClientRef.current = client;

    client.on("connect", () => {
      setMqttConnected(true);
      client.subscribe([MQTT_D2C_TOPIC, MQTT_STATE_TOPIC], (error) => {
        if (error) {
          toast.error("Failed to subscribe to MQTT updates");
        }
      });
    });

    client.on("message", handleMqttMessage);
    client.on("reconnect", () => setMqttConnected(false));
    client.on("close", () => setMqttConnected(false));
    client.on("error", (error) => {
      console.error("MQTT error:", error);
      setMqttConnected(false);
    });

    return () => {
      Object.values(pendingCommandTimersRef.current).forEach(clearTimeout);
      pendingCommandTimersRef.current = {};
      if (stateFlushTimerRef.current) {
        clearTimeout(stateFlushTimerRef.current);
        stateFlushTimerRef.current = null;
      }
      statePacketQueueRef.current = [];
      client.end(true);
      mqttClientRef.current = null;
    };
  }, [handleMqttMessage]);

  const loadDashboardData = useCallback(
    async (showToast = false) => {
      try {
        const data = await api.getDashboardData(selectedTower);
        setDashboardData(data);
        setStatusCheckTime(Date.now());

        if (data.towers && isSuperAdmin()) {
          setAllTowers((currentTowers) => {
            if (!selectedTower) {
              return data.towers;
            }

            return currentTowers.length > 0 ? currentTowers : data.towers;
          });
        }

        if (showToast) {
          toast.success("Dashboard refreshed");
        }
      } catch (error) {
        console.error("Error loading dashboard data:", error);
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    },
    [isSuperAdmin, selectedTower],
  );

  useEffect(() => {
    setLoading(true);
    loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setStatusCheckTime(Date.now());

      if (Object.keys(pendingCommandsRef.current).length === 0) {
        loadDashboardData();
      }
    }, LIVE_STATUS_REFRESH_MS);

    return () => clearInterval(intervalId);
  }, [loadDashboardData]);

  const publishCommand = ({
    deviceId,
    deviceType,
    command,
    mode,
    remoteMode,
    targetId,
    targetType,
    targetState,
  }) => {
    const client = mqttClientRef.current;

    if (!client?.connected) {
      toast.error("MQTT is not connected");
      return;
    }

    const payload = buildCommandPayload({
      deviceType,
      deviceId,
      command,
      mode,
      remoteMode,
      targetId,
      targetType,
      targetState,
    });

    setPendingCommands((current) => ({
      ...current,
      [payload.command_id]: true,
    }));

    client.publish(
      `${MQTT_C2D_PREFIX}/${deviceId}`,
      JSON.stringify(payload),
      { qos: 1 },
      (error) => {
        if (error) {
          setPendingCommands((current) => {
            const next = { ...current };
            delete next[payload.command_id];
            return next;
          });
          toast.error("Failed to send MQTT command");
        } else {
          toast.success("Command sent, waiting for acknowledgement");
        }
      },
    );
  };

  const publishValveToggleCommand = ({
    societyId,
    deviceId,
    valveId,
    operation,
  }) => {
    const client = mqttClientRef.current;

    if (!client?.connected) {
      toast.error("MQTT is not connected");
      return;
    }

    if (!societyId || !isUsableReference(deviceId) || !isUsableReference(valveId)) {
      toast.error("Missing valve command details");
      return;
    }

    const payload = buildValveTogglePayload({
      deviceId,
      valveId,
      operation,
    });

    const pendingCommand = {
      type: "valve",
      societyId,
      deviceId,
      targetId: valveId,
      operation,
      startedAt: Date.now(),
    };

    setPendingCommands((current) => ({
      ...current,
      [payload.mid]: pendingCommand,
    }));
    pendingCommandsRef.current = {
      ...pendingCommandsRef.current,
      [payload.mid]: pendingCommand,
    };

    pendingCommandTimersRef.current[payload.mid] = setTimeout(() => {
      completePendingCommand(payload.mid, {
        toastMessage: "Valve acknowledgement timed out",
        toastType: "error",
      });
    }, VALVE_ACK_TIMEOUT_MS);

    client.publish(
      `${MQTT_C2D_PREFIX}/${societyId}`,
      JSON.stringify(payload),
      { qos: 1 },
      (error) => {
        if (error) {
          clearPendingCommandImmediately(payload.mid);
          toast.error("Failed to send valve command");
        } else {
          toast.success("Valve command sent, waiting for acknowledgement");
        }
      },
    );
  };

  const publishPumpToggleCommand = ({
    societyId,
    deviceId,
    pumpId,
    operation,
  }) => {
    const client = mqttClientRef.current;

    if (!client?.connected) {
      toast.error("MQTT is not connected");
      return;
    }

    if (!societyId || !isUsableReference(deviceId) || !isUsableReference(pumpId)) {
      toast.error("Missing pump command details");
      return;
    }

    const payload = buildPumpTogglePayload({
      deviceId,
      pumpId,
      operation,
    });

    const pendingCommand = {
      type: "pump",
      societyId,
      deviceId,
      targetId: pumpId,
      operation,
      startedAt: Date.now(),
    };

    setPendingCommands((current) => ({
      ...current,
      [payload.mid]: pendingCommand,
    }));
    pendingCommandsRef.current = {
      ...pendingCommandsRef.current,
      [payload.mid]: pendingCommand,
    };

    pendingCommandTimersRef.current[payload.mid] = setTimeout(() => {
      completePendingCommand(payload.mid, {
        toastMessage: "Pump acknowledgement timed out",
        toastType: "error",
      });
    }, PUMP_ACK_TIMEOUT_MS);

    client.publish(
      `${MQTT_C2D_PREFIX}/${societyId}`,
      JSON.stringify(payload),
      { qos: 1 },
      (error) => {
        if (error) {
          clearPendingCommandImmediately(payload.mid);
          toast.error("Failed to send pump command");
        } else {
          toast.success("Pump command sent, waiting for acknowledgement");
        }
      },
    );
  };

  const publishRemoteOperationCommand = ({
    societyId,
    deviceId,
    controllerType,
    remoteOperations,
  }) => {
    const client = mqttClientRef.current;

    if (!client?.connected) {
      toast.error("MQTT is not connected");
      return;
    }

    if (!societyId || !isUsableReference(deviceId)) {
      toast.error("Missing remote operation details");
      return;
    }

    const payload = buildRemoteOperationsPayload({
      deviceId,
      remoteOperations,
    });

    const pendingCommand = {
      type: "remote",
      societyId,
      deviceId,
      controllerType,
      remoteOperations,
      startedAt: Date.now(),
    };

    setPendingCommands((current) => ({
      ...current,
      [payload.mid]: pendingCommand,
    }));
    pendingCommandsRef.current = {
      ...pendingCommandsRef.current,
      [payload.mid]: pendingCommand,
    };

    pendingCommandTimersRef.current[payload.mid] = setTimeout(() => {
      completePendingCommand(payload.mid, {
        toastMessage: "Remote operation acknowledgement timed out",
        toastType: "error",
      });
    }, controllerType === "pump" ? PUMP_ACK_TIMEOUT_MS : VALVE_ACK_TIMEOUT_MS);

    client.publish(
      `${MQTT_C2D_PREFIX}/${societyId}`,
      JSON.stringify(payload),
      { qos: 1 },
      (error) => {
        if (error) {
          clearPendingCommandImmediately(payload.mid);
          toast.error("Failed to send remote operation");
        } else {
          toast.success("Remote operation sent, waiting for acknowledgement");
        }
      },
    );
  };

  const towerCards = useMemo(() => {
    const data = Array.isArray(dashboardData?.data) ? dashboardData.data : [];
    const dataByTowerId = new Map(data.map((tower) => [tower.tower_id, tower]));

    if (selectedTower) {
      const selectedTowerMeta = allTowers.find(
        (tower) => tower.id === selectedTower,
      );
      const selectedTowerData =
        dataByTowerId.get(selectedTower) ||
        data.find((tower) => tower.tower_id === selectedTower);

      if (selectedTowerData) {
        return [
          {
            ...selectedTowerData,
            tower_id: selectedTower,
            tower_name:
              selectedTowerData.tower_name || selectedTowerMeta?.name || "-",
            tower_location:
              selectedTowerData.tower_location ||
              selectedTowerMeta?.location ||
              "-",
            is_active: selectedTowerMeta?.active ?? true,
            tanks: selectedTowerData.tanks || [],
          },
        ];
      }

      if (selectedTowerMeta) {
        return [
          {
            tower_id: selectedTowerMeta.id,
            tower_name: selectedTowerMeta.name,
            tower_location: selectedTowerMeta.location,
            is_active: Boolean(selectedTowerMeta.active),
            tanks: [],
          },
        ];
      }

      return [];
    }

    if (isSuperAdmin() && allTowers.length > 0) {
      return allTowers.map((tower) => ({
        ...dataByTowerId.get(tower.id),
        tower_id: tower.id,
        tower_name: dataByTowerId.get(tower.id)?.tower_name || tower.name,
        tower_location:
          dataByTowerId.get(tower.id)?.tower_location || tower.location,
        is_active: Boolean(tower.active),
        tanks: dataByTowerId.get(tower.id)?.tanks || [],
      }));
    }

    return data.map((tower) => ({ ...tower, is_active: true }));
  }, [allTowers, dashboardData, isSuperAdmin, selectedTower]);

  if (loading) {
    return (
      <Layout>
        <div className="flex min-h-[60vh] items-center justify-center">
          <div className="flex items-center gap-3 text-lg text-slate-600">
            <Loader className="h-5 w-5 animate-spin" />
            Loading...
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="dashboard-page">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900">Dashboard</h1>
            <p className="mt-1 text-slate-600">
              Real-time monitoring of water infrastructure
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {isSuperAdmin() && allTowers.length > 0 && (
              <Select
                value={selectedTower || "all"}
                onValueChange={(val) =>
                  setSelectedTower(val === "all" ? null : val)
                }
              >
                <SelectTrigger className="w-[200px]" data-testid="tower-selector">
                  <SelectValue placeholder="Select Tower" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Towers</SelectItem>
                  {allTowers.map((tower) => (
                    <SelectItem key={tower.id} value={tower.id}>
                      {tower.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Button
              variant="outline"
              className="gap-2"
              onClick={() => loadDashboardData(true)}
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>

        {towerCards.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {towerCards.map((tower) => (
              <TowerCard
                key={tower.tower_id}
                tower={tower}
                mqttConnected={mqttConnected}
                pendingCommands={pendingCommands}
                publishCommand={publishCommand}
                publishValveToggleCommand={publishValveToggleCommand}
                publishPumpToggleCommand={publishPumpToggleCommand}
                publishRemoteOperationCommand={publishRemoteOperationCommand}
                statusCheckTime={statusCheckTime}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-3xl border border-slate-200 bg-white p-12 text-center shadow-sm">
            <Building2 className="mx-auto mb-4 h-16 w-16 text-slate-300" />
            <h3 className="mb-2 text-xl font-semibold text-slate-900">
              No Data Available
            </h3>
            <p className="text-slate-600">
              {isSuperAdmin()
                ? "No towers found. Please register devices in the Device Registration section."
                : "No devices have been mapped to your account yet. Contact your Super Admin."}
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};

const TowerCard = ({
  tower,
  mqttConnected,
  pendingCommands,
  publishCommand,
  publishValveToggleCommand,
  publishPumpToggleCommand,
  publishRemoteOperationCommand,
  statusCheckTime,
}) => {
  const tanks = tower.tanks || [];
  const levelController = tower.level_controller;
  const pumpController = tower.pump_controller;
  const isLevelControllerActive = isControllerActive(
    levelController?.timestamp,
    statusCheckTime,
  );
  const isPumpControllerActive = isControllerActive(
    pumpController?.timestamp,
    statusCheckTime,
  );
  const hasActiveController = isLevelControllerActive || isPumpControllerActive;
  const battery =
    levelController?.battery_level ??
    pumpController?.battery_level ??
    tanks[0]?.level_controller_battery;
  const hasFault = tanks.some(
    (tank) => tank.pump_packet?.pump_health && tank.pump_packet.pump_health !== "no_fault",
  );

  return (
    <div
      className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-md shadow-slate-200/60"
      data-testid={`tower-card-${tower.tower_id}`}
    >
      <div className="flex items-start justify-between border-b border-slate-100 px-4 py-3 sm:px-5">
        <div className="flex items-start gap-3">
          <span
            className={`mt-1.5 h-3 w-3 rounded-full ${hasActiveController ? "bg-green-500 shadow-[0_0_0_5px_rgba(34,197,94,0.12)]" : "bg-slate-300"}`}
          />
          <div>
            <h2 className="text-lg font-bold text-slate-950">
              {tower.tower_name}
            </h2>
            <p className="mt-0.5 text-sm font-medium text-slate-400">
              {tower.tower_location || "-"}
            </p>
          </div>
        </div>

        <div className="flex flex-col items-end gap-1.5 rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2">
          <ControllerStatusBadge
            label="Level"
            active={isLevelControllerActive}
          />
          <ControllerStatusBadge
            label="Pump"
            active={isPumpControllerActive}
          />
        </div>
      </div>

      <div className="space-y-3 px-4 py-4 sm:px-5">
        {tanks.length > 0 ? (
          tanks.map((tank, index) => (
            <div
              key={`${tank.tank_id}-${tank.valve_id}`}
              className={index > 0 ? "border-t border-slate-200 pt-5 mt-5" : ""}
            >
              <TankPanel
                tank={tank}
                tower={tower}
                mqttConnected={mqttConnected}
                pendingCommands={pendingCommands}
                publishCommand={publishCommand}
                publishValveToggleCommand={publishValveToggleCommand}
                publishPumpToggleCommand={publishPumpToggleCommand}
                publishRemoteOperationCommand={publishRemoteOperationCommand}
              />
            </div>
          ))
        ) : (
          <div className="py-7 text-center text-lg text-slate-400">
            No tanks configured
          </div>
        )}
      </div>

      {tanks.length > 0 && (
        <div className="flex items-center gap-5 border-t border-slate-100 px-4 py-3 text-xs sm:px-5">
          <span className="flex items-center gap-2 font-semibold text-slate-600">
            <Battery className="h-3.5 w-3.5" />
            {formatWholePercent(battery)}%
          </span>
          <span
            className={`flex items-center gap-2 font-semibold ${hasFault ? "text-red-500" : "text-emerald-500"}`}
          >
            <Heart className="h-3.5 w-3.5" />
            {hasFault ? "Fault" : "Healthy"}
          </span>
        </div>
      )}
    </div>
  );
};

const ControllerStatusBadge = ({ label, active }) => (
  <div className="flex items-center gap-2 text-xs font-semibold text-slate-600">
    <span
      className={`h-2 w-2 rounded-full ${active ? "bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.12)]" : "bg-slate-300"}`}
    />
    <span className="min-w-[34px] text-slate-500">{label}</span>
    <span className={active ? "text-emerald-600" : "text-slate-400"}>
      {active ? "Active" : "Inactive"}
    </span>
  </div>
);

const TankPanel = ({
  tank,
  tower,
  mqttConnected,
  pendingCommands,
  publishCommand,
  publishValveToggleCommand,
  publishPumpToggleCommand,
  publishRemoteOperationCommand,
}) => {
  const levelDeviceId = getUsableReference(
    tank.level_controller_device_id,
    tower.level_controller?.device_id,
  );
  const pumpDeviceId = getUsableReference(tower.pump_controller?.device_id);
  const valveId = getUsableReference(tank.valve_id);
  const valveDisplayId = getUsableReference(
    tank.valve_custom_id,
    tank.valve_id,
  );
  const pumpPacket = tank.pump_packet;
  const pumpPacketId = getUsableReference(pumpPacket?.pump_id);
  const pumpId =
    pumpPacketId ||
    getUsableReference(tank.pump?.pump_custom_id, tank.pump?.pump_id);
  const percentage = formatWholePercent(tank.tank_percentage);
  const tankHigh = Number(tank.high);
  const isTankAboveHigh =
    typeof percentage === "number" &&
    Number.isFinite(tankHigh) &&
    percentage > tankHigh;
  const valveState = getUsableReference(tank.valve_state);
  const pumpState = getUsableReference(pumpPacket?.pump_state);
  const rawValveOn = isOn(tank.valve_state);
  const rawPumpOn = isOn(pumpState);
  const levelOperationMode = normalize(
    tank.level_controller_mode || tower.level_controller?.operation_mode,
  );
  const levelRemoteMode = normalize(
    tank.level_controller_remote || tower.level_controller?.remote_mode,
  );
  const pumpOperationMode = normalize(
    pumpPacket?.atbc_mode || tower.pump_controller?.operation_mode,
  );
  const pumpRemoteMode = normalize(
    pumpPacket?.atbc_remote || tower.pump_controller?.remote_mode,
  );
  const hasLevelControllerPacket = Boolean(tower.level_controller && levelDeviceId);
  const hasValveCommandTarget = Boolean(valveId);
  const hasPumpControllerPacket = Boolean(tower.pump_controller && pumpDeviceId);
  const hasPumpPacket = Boolean(pumpPacket && pumpPacketId);
  const hasValveStatus = Boolean(
    hasLevelControllerPacket && hasValveCommandTarget && valveState,
  );
  const hasPumpStatus = Boolean(
    hasPumpControllerPacket && hasPumpPacket && pumpState,
  );
  const valveOn = hasValveStatus && rawValveOn && !isTankAboveHigh;
  const pumpOn = hasPumpStatus && rawPumpOn;
  const canControlValve = Boolean(
    hasLevelControllerPacket &&
      hasValveCommandTarget &&
      mqttConnected &&
      levelRemoteMode === "on" &&
      (valveOn || !isTankAboveHigh),
  );
  const canSendValveRemote = Boolean(
    hasLevelControllerPacket && hasValveCommandTarget && mqttConnected,
  );
  const canControlPump = Boolean(
    hasPumpControllerPacket &&
      hasPumpPacket &&
      mqttConnected &&
      isOn(pumpRemoteMode),
  );
  const canSendPumpRemote = Boolean(
    hasPumpControllerPacket && hasPumpPacket && mqttConnected,
  );
  const pendingCommandList = Object.values(pendingCommands);
  const valvePending = pendingCommandList.some(
    (command) =>
      (command.type === "valve" &&
        command.deviceId === levelDeviceId &&
        command.targetId === valveId) ||
      (command.type === "remote" &&
        command.controllerType === "level" &&
        command.deviceId === levelDeviceId),
  );
  const pumpPending = pendingCommandList.some(
    (command) =>
      (command.type === "pump" &&
        command.deviceId === pumpDeviceId &&
        command.targetId === pumpPacketId) ||
      (command.type === "remote" &&
        command.controllerType === "pump" &&
        command.deviceId === pumpDeviceId),
  );

  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-[82px_1fr_auto] md:items-center">
        <div className="flex justify-center md:block">
          <TankGauge percentage={percentage} timestamp={tower.level_controller?.timestamp} />
        </div>

        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <h3 className="truncate text-lg font-bold text-slate-950">
              {tank.tank_name || tank.tank_custom_id || tank.tank_id}
            </h3>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-400">
            <SlidersHorizontal className="h-3.5 w-3.5" />
            {levelDeviceId || "-"}
          </div>
          <StatusLine
            active={valveOn}
            label={hasValveStatus ? `Valve ${valveOn ? "ON" : "OFF"}` : "Valve N/A"}
            timestamp={tower.level_controller?.timestamp}
          />
          <StatusLine
            active={pumpOn}
            label={hasPumpStatus ? `Pump ${pumpOn ? "ON" : "OFF"}` : "Pump N/A"}
            timestamp={tower.pump_controller?.timestamp}
          />
        </div>

        <div className="hidden rounded-full bg-red-50 p-3 text-red-500 md:block">
          <Droplet className="h-5 w-5 fill-current" />
        </div>
      </div>

      <ControlRow
        type="valve"
        title={formatControlTitle("Valve", valveDisplayId)}
        icon={<SlidersHorizontal className="h-5 w-5" />}
        mode={levelOperationMode}
        remoteMode={levelRemoteMode}
        checked={valveOn}
        disabled={!canControlValve}
        remoteDisabled={!canSendValveRemote}
        pending={valvePending}
        onRemoteToggle={(enabled) => {
            publishRemoteOperationCommand({
              societyId: tower.society_id,
              deviceId: levelDeviceId,
              controllerType: "level",
              remoteOperations: enabled ? "on" : "off",
            });
        }}
        onToggle={(checked) =>
          publishValveToggleCommand({
            societyId: tower.society_id,
            deviceId: levelDeviceId,
            valveId,
            operation: checked ? "open" : "close",
          })
        }
      />

      <ControlRow
        type="pump"
        title={formatControlTitle("Pump", pumpId)}
        icon={<Power className="h-5 w-5" />}
        mode={hasPumpControllerPacket && hasPumpPacket ? pumpOperationMode : ""}
        remoteMode={hasPumpControllerPacket && hasPumpPacket ? pumpRemoteMode : ""}
        ugtId={pumpPacket?.ugt_id}
        ugtLevel={pumpPacket?.ugt_level}
        checked={pumpOn}
        disabled={!canControlPump}
        remoteDisabled={!canSendPumpRemote}
        pending={pumpPending}
        onRemoteToggle={(enabled) => {
            publishRemoteOperationCommand({
              societyId: tower.society_id,
              deviceId: pumpDeviceId,
              controllerType: "pump",
              remoteOperations: enabled ? "on" : "off",
            });
        }}
        onToggle={(checked) =>
          publishPumpToggleCommand({
            societyId: tower.society_id,
            deviceId: pumpDeviceId,
            pumpId: pumpPacketId,
            operation: checked ? "on" : "off",
          })
        }
      />
    </div>
  );
};

const TankGauge = ({ percentage, timestamp }) => {
  const fillHeight =
    typeof percentage === "number" ? Math.max(0, Math.min(100, percentage)) : 0;

  return (
    <div className="w-[82px] text-center">
      <div className="relative mx-auto h-[86px] w-[64px] overflow-hidden rounded-[18px] border-2 border-slate-200 bg-slate-50 shadow-inner">
        <div
          className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-blue-500 to-blue-200 transition-all"
          style={{ height: `${fillHeight}%` }}
        />
        <div className="absolute inset-0 flex items-center justify-center text-lg font-extrabold text-slate-800">
          {typeof percentage === "number" ? `${percentage}%` : "-"}
        </div>
      </div>
      <p className="mt-1.5 text-[10px] font-semibold text-slate-400">
        {formatTimestamp(timestamp)}
      </p>
    </div>
  );
};

const StatusLine = ({ active, label, timestamp }) => (
  <div className="mt-1.5 grid grid-cols-[1fr_auto] gap-3 text-xs">
    <span className="flex items-center gap-2 font-semibold text-slate-600">
      <span
        className={`h-2.5 w-2.5 rounded-full ${active ? "bg-green-500" : "bg-slate-300"}`}
      />
      {label}
    </span>
    <span className="text-slate-400">{formatTimestamp(timestamp)}</span>
  </div>
);

const ControlRow = ({
  type,
  title,
  icon,
  mode,
  remoteMode,
  ugtId,
  ugtLevel,
  checked,
  disabled,
  remoteDisabled,
  pending,
  onRemoteToggle,
  onToggle,
}) => {
  const isSwitchDisabled = disabled || pending;
  const isRemoteDisabled = remoteDisabled || pending;
  const availableModeOptions =
    type === "valve"
      ? modeOptions.filter((option) => option !== "manual")
      : modeOptions;
  const normalizedMode = normalize(mode);
  const activeMode = isOn(remoteMode)
    ? "remote"
    : type === "valve" && normalizedMode === "manual"
      ? "auto"
      : normalizedMode;
  const noAccess = disabled && remoteDisabled;
  const canChangeRemoteMode = !isRemoteDisabled;
  const showUgtLevel = type === "pump";
  const iconClass = disabled
    ? "bg-slate-200 text-slate-400"
    : type === "valve"
      ? "bg-orange-400 text-white"
      : "bg-blue-500 text-white";
  const titleClass = disabled ? "text-slate-500" : "text-slate-900";
  const ugtBadgeClass = disabled
    ? "border-slate-200 bg-slate-100 text-slate-400"
    : "border-blue-100 bg-white text-slate-600 shadow-sm";

  return (
    <div
      className={`flex flex-col gap-3 rounded-2xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${
        disabled
          ? "border-slate-100 bg-slate-100/70"
          : "border-slate-100 bg-slate-50"
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`flex h-11 w-11 items-center justify-center rounded-xl ${iconClass}`}
        >
          {icon}
        </div>
        <div>
          <h4 className={`text-base font-bold ${titleClass}`}>{title}</h4>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {availableModeOptions.map((option) => (
              <button
                key={option}
                type="button"
                disabled={
                  !canChangeRemoteMode ||
                  (option !== "auto" && option !== "remote") ||
                  activeMode === option
                }
                onClick={() => {
                  if (option === "remote") {
                    onRemoteToggle(true);
                  } else if (option === "auto") {
                    onRemoteToggle(false);
                  }
                }}
                className={`rounded-md border px-2.5 py-0.5 text-[11px] font-semibold capitalize transition ${
                  noAccess
                    ? activeMode === option
                      ? "border-slate-300 bg-slate-200/70 text-slate-500"
                      : "border-slate-200 bg-slate-100/50 text-slate-300"
                    : activeMode === option
                    ? "border-cyan-300 bg-cyan-50 text-slate-900"
                    : "border-slate-200 bg-white text-slate-400"
                } ${
                  canChangeRemoteMode &&
                  (option === "auto" || option === "remote") &&
                  activeMode !== option
                    ? "cursor-pointer hover:border-cyan-200 hover:text-slate-700"
                    : "cursor-default pointer-events-none"
                }`}
              >
                {option}
              </button>
            ))}
          </div>
          {showUgtLevel && (
            <div
              className={`mt-2 inline-flex items-center gap-2 rounded-lg border px-2.5 py-1 text-xs font-semibold ${ugtBadgeClass}`}
            >
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-md ${
                  disabled ? "bg-slate-200 text-slate-400" : "bg-blue-50 text-blue-600"
                }`}
              >
                <Waves className="h-3.5 w-3.5" />
              </span>
              <span className={disabled ? "text-slate-400" : "text-slate-500"}>
                UGT {ugtId && ugtId !== "-" ? ugtId : "Level"}
              </span>
              <span className={disabled ? "text-slate-400" : "text-blue-700"}>
                {formatUgtLevel(ugtLevel)}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 sm:justify-end">
        {pending && (
          <span className="flex items-center gap-1.5 text-xs font-semibold text-cyan-700">
            <Loader className="h-3.5 w-3.5 animate-spin" />
            Processing...
          </span>
        )}
        <Switch checked={checked} disabled={isSwitchDisabled} onCheckedChange={onToggle} />
      </div>
    </div>
  );
};

export default Dashboard;
